import { z } from 'zod';
import { pipe, Option, Array as A } from 'effect';

// Beneficiary "source" — where the record came from.
export const BeneficiarySourceSchema = z.enum(['erp', 'user']);
export type BeneficiarySource = z.infer<typeof BeneficiarySourceSchema>;

// Provider-specific key/value bag (account_number, account_currency,
// account_type...). Backend-driven and open-ended → loose string map.
// The backend sends null, not an absent key, for a detail it has no value for;
// normalised to undefined because these go straight in as form initial values.
export const BeneficiaryDetailsSchema = z.record(
  z.string(),
  z
    .string()
    .nullable()
    .optional()
    .transform((value) => value ?? undefined),
);

// GET beneficiaries/{id} — confirmed against a live response. NOTE: the entity has
// NO vendorDisplayName (that lives on the grid ROW, not the entity). details keys +
// overall shape vary by payment provider → passthrough(); the dev [API SCHEMA]
// validator surfaces drift to tighten.
export const BeneficiarySchema = z
  .object({
    id: z.string(),
    vendorId: z.string(),
    providerName: z.string(),
    source: BeneficiarySourceSchema,
    isDefault: z.boolean(),
    details: BeneficiaryDetailsSchema,
  })
  .passthrough();
export type Beneficiary = z.infer<typeof BeneficiarySchema>;

// GET beneficiaries/form — confirmed: { formTitle, formSubTitle, fields[] }.
// Field set/types vary by provider; passthrough() for provider extras. The
// `type` enum mirrors the modal's local BeneficiaryField type + its renderer —
// if a provider ever sends a new type, the dev [API SCHEMA] validator surfaces
// it (extend this enum AND the modal's ./types together).
//
// IBAN account support (DPM-497 / DPM-639; pattern mirrors the DPM-545
// open_finance add-account form): the backend may drive conditional fields via
// `visibleWhen` (rendered/validated/submitted only when the referenced field
// equals the given value). `type: 'iban'` is a read-only preview whose value is
// assembled on the client from `computedFrom` parts as an Israeli IBAN (IL +
// mod-97 check digits + zero-padded BBAN). NB: no validation rules travel in the
// response — validation lives on the client, keyed by field name (see the
// modal's config.ts).
export const BeneficiaryFieldVisibleWhenSchema = z.object({
  field: z.string(),
  equals: z.string(),
});

// One option entry shape, used by `options` in both its flat and per-parent map
// form. After normalisation a dependent field's option also carries the parent
// field's value under the parent field's own name (e.g. `bank_code: '054'`),
// which is why the normalised option allows extra string keys.
// `catchall` is load-bearing, not defensive: a normalised dependent option carries
// its parent value under a key whose NAME comes from the payload (`dependsOn[0]`),
// so it cannot be declared. A plain z.object would strip that tag on re-parse and
// silently break option scoping.
export const BeneficiaryFieldOptionSchema = z.object({ label: z.string(), value: z.string() }).catchall(z.string());
export type BeneficiaryFieldOption = z.infer<typeof BeneficiaryFieldOptionSchema>;

// Raw field exactly as the backend sends it.
export const BeneficiaryFieldRawSchema = z
  .object({
    name: z.string(),
    label: z.string(),
    type: z.enum(['text', 'email', 'select', 'multi-select', 'iban', 'autocomplete']),
    placeholder: z.string().optional(),
    helpText: z.string().optional(),
    subscription: z.string().optional(),
    // TWO SHAPES, one key. Independent fields send a flat list; a field whose
    // values depend on another field sends a map keyed by the parent's value:
    //   bank_code:   options: [ { label, value }, ... ]
    //   branch_code: options: { '054': [ ...branches of bank 054 ], ... }
    // The union is mandatory — declaring only the array form makes the whole
    // form response fail to parse.
    options: z
      .union([z.array(BeneficiaryFieldOptionSchema), z.record(z.string(), z.array(BeneficiaryFieldOptionSchema))])
      .optional(),
    // Names of the fields this field's options depend on. `dependsOn[0]` is both
    // the parent whose value scopes the options and the key each normalised
    // option carries its parent value under.
    dependsOn: z.array(z.string()).optional(),
    defaultValue: z.union([z.string(), z.array(z.string())]).optional(),
    readOnly: z.boolean().optional(),
    computedFrom: z.array(z.string()).optional(),
    // `type: 'iban'` may set `hidden: true` — the value is still assembled from
    // `computedFrom` and submitted, but the preview control is not rendered.
    hidden: z.boolean().optional(),
    visibleWhen: BeneficiaryFieldVisibleWhenSchema.optional(),
  })
  .passthrough();

type RawBeneficiaryField = z.infer<typeof BeneficiaryFieldRawSchema>;

type OptionMap = Record<string, BeneficiaryFieldOption[]>;

const isOptionMap = (options: unknown): options is OptionMap =>
  !!options && !Array.isArray(options) && typeof options === 'object';

// Collapse a per-parent option map into one flat array, tagging each entry with
// the map key under the parent field's name, so `options` is an array on every
// field.
// Idempotent: a field whose `options` is already a flat array is returned as-is.
const flattenParentScopedOptions = (field: RawBeneficiaryField) =>
  pipe(
    Option.all([
      Option.fromNullable(isOptionMap(field.options) ? (field.options as OptionMap) : undefined),
      Option.fromNullable(field.dependsOn?.[0]),
    ]),
    Option.map(([map, parentName]) =>
      pipe(
        Object.entries(map),
        A.flatMap(([parentValue, options]) =>
          pipe(
            options,
            A.map((option) => ({ ...option, [parentName]: parentValue })),
          ),
        ),
        (tagged) => ({ ...field, options: tagged }),
      ),
    ),
    // No map (or no declared parent) — keep the flat array as it came.
    Option.getOrElse(() => ({
      ...field,
      options: Array.isArray(field.options) ? field.options : undefined,
    })),
  );

// Normalised field — what every consumer above the API boundary sees. `options`
// is always a flat array; a dependent field's entries each carry their parent value.
export const BeneficiaryFieldSchema = BeneficiaryFieldRawSchema.transform(flattenParentScopedOptions);
export type BeneficiaryField = z.output<typeof BeneficiaryFieldSchema>;

export const BeneficiaryFormSchema = z
  .object({
    formTitle: z.string(),
    formSubTitle: z.string(),
    fields: z.array(BeneficiaryFieldSchema),
  })
  .passthrough();
export type BeneficiaryForm = z.output<typeof BeneficiaryFormSchema>;

// Path / query.
export const BeneficiaryIdPathSchema = z.object({ id: z.string().min(1) });
export const BeneficiaryDetailsQuerySchema = z.object({ isExternal: z.boolean() }).partial();
export const VendorIdPathSchema = z.object({ vendorId: z.string().min(1) });

// Request bodies — camelCase on both create and update (requests now route through
// the AP host, which expects camelCase).
// `isDefault` is optional, not required: the payment flow's form omits it entirely
// (DPM-661), while the beneficiaries settings list still sends it on create.
export const BeneficiaryCreateBodySchema = z.object({
  details: BeneficiaryDetailsSchema,
  vendorId: z.string(),
  isDefault: z.boolean().optional(),
  source: BeneficiarySourceSchema,
});
export const BeneficiaryUpdateBodySchema = z
  .object({
    details: BeneficiaryDetailsSchema,
    isDefault: z.boolean(),
    source: BeneficiarySourceSchema,
  })
  .partial();
// Vendor field: body keyed by grid field name (withholdingTaxRate | withholdingTaxExpiryDate).
export const VendorFieldBodySchema = z.record(z.string(), z.union([z.string(), z.number(), z.null()]));
export const VendorFieldResponseSchema = z.object({ updatedPaymentsCount: z.number() }).passthrough();

// POST beneficiaries returns ONLY the new id — NOT the full entity (that's the
// GET beneficiaries/{id} shape, BeneficiarySchema). Payment-API writes return
// minimal bodies; we only consume `.id`. passthrough() tolerates extra fields.
export const BeneficiaryCreateResponseSchema = z.object({ id: z.string() }).passthrough();

// Column `type` is an enum ON PURPOSE: a backend grid column whose type we don't
// render must surface (dev [API SCHEMA] throw) — the "control unknown header types"
// guard. When you add a type, extend this enum AND the grid's column-type→renderer
// mapping together. (Observed: string/amount/percent/date.)
export const GridColumnTypeSchema = z.enum(['string', 'amount', 'percent', 'date']);
export const PaymentGridColumnSchema = z
  .object({
    field: z.string(),
    headerName: z.string(),
    type: GridColumnTypeSchema,
    sortable: z.boolean(),
    filter: z.boolean(),
    hide: z.boolean(),
    operator: z.string().nullable(),
    editable: z.boolean().optional(),
  })
  .passthrough();
export const BeneficiariesGridHeadersSchema = z.object({
  vendor: z.array(PaymentGridColumnSchema),
  beneficiary: z.array(PaymentGridColumnSchema),
});

// GET grid/beneficiaries/total returns a BARE number (e.g. 7), not an object.
export const BeneficiariesGridTotalSchema = z.number();

// Backend-driven grid; rows vary by provider → loose rows, typed envelope.
export const BeneficiariesGridSearchBodySchema = z
  .object({
    filterModel: z.unknown().optional(),
    sortModel: z.unknown().optional(),
    paginationModel: z.object({ limit: z.number(), offset: z.number() }),
    searchTerm: z.string().optional(),
  })
  .passthrough();
export const BeneficiariesGridSearchResponseSchema = z.object({
  items: z.array(z.record(z.string(), z.unknown())),
  count: z.number(),
});
